/**
 * Member List Component (T070-T071, US9)
 *
 * Displays a table of branch members with their roles and join dates.
 * Allows the branch owner to remove non-owner members.
 *
 * Features:
 * - Display members table with user details
 * - Show role (owner vs. member)
 * - Remove button for non-owners (owner only)
 * - Refresh list after changes
 */

import { showInviteModal } from './invite-modal'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../core/firebase'

export interface MemberInfo {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  role: 'owner' | 'member'
  joinedAt: string | null
  createdAt: string | null
  lastSignIn: string | null
}

/**
 * Render member list component
 * @param containerId The ID of the container element
 * @param branchId The branch ID to list members for
 * @param branchName The branch name for display
 * @param currentUserUid The current user's UID (to determine permissions)
 */
export async function renderMemberList(
  containerId: string,
  branchId: string,
  branchName: string,
  currentUserUid: string,
): Promise<void> {
  const container = document.getElementById(containerId)

  if (!container) {
    console.error(`[MEMBER_LIST] Container not found: ${containerId}`)
    return
  }

  // T030: Check if user has access to member invites feature
  const { checkFeatureAccess } = await import('../tier/feature-gate')
  const { allowed: canInvite } = checkFeatureAccess('memberInvites')

  // T030: Conditionally render invite button based on tier
  const inviteButtonHTML = canInvite
    ? `
        <button
          id="invite-member-btn"
          class="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          Invite Member
        </button>
      `
    : `
        <div class="flex items-center gap-2 text-gray-500">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
          </svg>
          <span class="text-sm">Member invites available on Standard and Enterprise plans</span>
        </div>
      `

  // Create container structure
  container.innerHTML = `
    <div class="bg-white rounded-lg shadow-md p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">Branch Members</h2>
          <p class="text-sm text-gray-600 mt-1">${branchName}</p>
        </div>
        ${inviteButtonHTML}
      </div>

      <!-- Loading state -->
      <div id="member-list-loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <p class="text-gray-600 mt-2">Loading members...</p>
      </div>

      <!-- Error state -->
      <div id="member-list-error" class="hidden p-4 bg-red-50 border border-red-200 rounded-lg">
        <p class="text-sm text-red-600"></p>
      </div>

      <!-- Members table -->
      <div id="member-list-table" class="hidden overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Member
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Sign In
              </th>
              <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody id="member-list-tbody" class="bg-white divide-y divide-gray-200">
            <!-- Members will be inserted here -->
          </tbody>
        </table>
      </div>

      <!-- Empty state -->
      <div id="member-list-empty" class="hidden text-center py-8">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
        <p class="text-gray-600 mt-4">Only you have access to this branch</p>
        <button
          id="invite-first-member-btn"
          class="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
        >
          Invite Your First Member
        </button>
      </div>
    </div>
  `

  // Add event listeners
  const inviteBtn = container.querySelector('#invite-member-btn')
  const inviteFirstBtn = container.querySelector('#invite-first-member-btn')

  inviteBtn?.addEventListener('click', () => {
    showInviteModal(branchId, branchName)
  })

  inviteFirstBtn?.addEventListener('click', () => {
    showInviteModal(branchId, branchName)
  })

  // Load members
  await loadMembers(branchId, currentUserUid, container)
}

/**
 * Load and display members
 */
async function loadMembers(
  branchId: string,
  currentUserUid: string,
  container: HTMLElement,
): Promise<void> {
  const loadingDiv = container.querySelector('#member-list-loading') as HTMLElement
  const errorDiv = container.querySelector('#member-list-error') as HTMLElement
  const errorText = errorDiv?.querySelector('p') as HTMLElement
  const tableDiv = container.querySelector('#member-list-table') as HTMLElement
  const emptyDiv = container.querySelector('#member-list-empty') as HTMLElement
  const tbody = container.querySelector('#member-list-tbody') as HTMLElement

  try {
    // T070: Wire up listMembers callable
    const listMembers = httpsCallable<
      { branchId: string, limit?: number, offset?: number },
      {
        success: boolean
        members: MemberInfo[]
        pagination: {
          limit: number
          offset: number
          total: number
          hasMore: boolean
        }
        branch: {
          id: string
          name: string
          planType: string
        }
      }
    >(functions, 'listMembers')

    const result = await listMembers({ branchId, limit: 100, offset: 0 })

    if (!result.data.success) {
      throw new Error('Failed to load members')
    }

    const members = result.data.members

    console.log('Members loaded:', members.length)

    // Hide loading
    loadingDiv.classList.add('hidden')

    // Show table or empty state
    if (members.length === 1 && members[0].role === 'owner') {
      // Only owner - show empty state
      emptyDiv.classList.remove('hidden')
    } else if (members.length > 0) {
      // Has members - show table
      tableDiv.classList.remove('hidden')
      renderMemberRows(members, currentUserUid, branchId, tbody)
    } else {
      // No members at all (shouldn't happen)
      emptyDiv.classList.remove('hidden')
    }
  } catch (error) {
    console.error('[MEMBER_LIST] Error loading members:', error)
    loadingDiv.classList.add('hidden')
    errorDiv.classList.remove('hidden')
    errorText.textContent = error instanceof Error ? error.message : 'Failed to load members'
  }
}

/**
 * Render member rows in the table
 */
function renderMemberRows(
  members: MemberInfo[],
  currentUserUid: string,
  branchId: string,
  tbody: HTMLElement,
): void {
  tbody.innerHTML = ''

  const currentUserIsOwner = members.some(
    (m) => m.uid === currentUserUid && m.role === 'owner',
  )

  members.forEach((member) => {
    const row = document.createElement('tr')
    row.className = 'hover:bg-gray-50 transition-colors'

    // Format dates
    const joinedDate = member.joinedAt
      ? new Date(member.joinedAt).toLocaleDateString()
      : 'N/A'
    const lastSignIn = member.lastSignIn
      ? new Date(member.lastSignIn).toLocaleDateString()
      : 'N/A'

    // Role badge
    const roleBadge
      = member.role === 'owner'
        ? '<span class="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">Owner</span>'
        : '<span class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Member</span>'

    // Actions (only for non-owners, and only if current user is owner)
    let actions = ''
    if (member.role !== 'owner' && currentUserIsOwner) {
      actions = `
        <button
          class="remove-member-btn text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
          data-user-id="${member.uid}"
          data-user-email="${member.email || 'Unknown'}"
        >
          Remove
        </button>
      `
    }

    row.innerHTML = `
      <td class="px-6 py-4 whitespace-nowrap">
        <div class="flex items-center">
          <div class="flex-shrink-0 h-10 w-10">
            ${
              member.photoURL
                ? `<img class="h-10 w-10 rounded-full" src="${member.photoURL}" alt="${member.displayName || 'User'}" />`
                : `<div class="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
                    ${(member.displayName || member.email || '?')[0].toUpperCase()}
                  </div>`
            }
          </div>
          <div class="ml-4">
            <div class="text-sm font-medium text-gray-900">
              ${member.displayName || 'Unknown User'}
            </div>
            <div class="text-sm text-gray-500">
              ${member.email || 'No email'}
            </div>
          </div>
        </div>
      </td>
      <td class="px-6 py-4 whitespace-nowrap">
        ${roleBadge}
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        ${joinedDate}
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        ${lastSignIn}
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        ${actions}
      </td>
    `

    tbody.appendChild(row)
  })

  // Add remove member event listeners
  const removeButtons = tbody.querySelectorAll('.remove-member-btn')
  removeButtons.forEach((btn) => {
    btn.addEventListener('click', async () => {
      const userId = btn.getAttribute('data-user-id')
      const userEmail = btn.getAttribute('data-user-email')

      if (!userId) return

      const confirmed = confirm(
        `Are you sure you want to remove ${userEmail} from this branch? They will lose access immediately.`,
      )

      if (!confirmed) return

      await removeMember(branchId, userId, btn as HTMLButtonElement, tbody)
    })
  })
}

/**
 * Remove a member from the branch
 */
async function removeMember(
  branchId: string,
  targetUserId: string,
  button: HTMLButtonElement,
  tbody: HTMLElement,
): Promise<void> {
  const originalText = button.textContent
  button.textContent = 'Removing...'
  button.disabled = true

  try {
    // T071: Wire up removeMember callable
    const removeMember = httpsCallable<
      { branchId: string, targetUserId: string },
      {
        success: boolean
        removedUserId: string
        branchId: string
        branchName: string
      }
    >(functions, 'removeMember')

    const result = await removeMember({ branchId, targetUserId })

    if (!result.data.success) {
      throw new Error('Failed to remove member')
    }

    console.log('Member removed:', result.data)

    // Remove the row from the table
    const row = button.closest('tr')
    row?.remove()

    // Check if table is now empty
    if (tbody.children.length === 0) {
      window.location.reload() // Refresh to show empty state
    }
  } catch (error) {
    console.error('[MEMBER_LIST] Error removing member:', error)
    alert(error instanceof Error ? error.message : 'Failed to remove member')
    button.textContent = originalText
    button.disabled = false
  }
}
